import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState } from '../store';
import { setEmployees, setSelectedEmployee } from '../store/employeesSlice';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks, Breakpoints } from '../constants/theme';
import { Employee, DevelopmentSuggestion } from '../types';
import SuccessionReadinessMeter from '../components/SuccessionReadinessMeter';
import { InteractiveButton } from '../components/MicroInteractions';
import { AccessibilityUtils } from '../utils/accessibility';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { filterEmployees, getRoleDashboardConfig } from '../utils/roleBasedAccess';
import { EmployeeService } from '../services/employeeService';
import LoadingSpinner from '../components/LoadingSpinner';
import DataConsistencyService from '../services/DataConsistencyService';

// Ultra-Modern Professional Canvas
const ScreenContainer = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  position: relative;
  padding: 0;
  overflow-x: hidden;
  font-family: ${FontStacks.primary};
  
  /* Professional container layout */
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: ${Spacing.xxxl}px ${Spacing.xl}px;
  }
`;

const ProfileHeader = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxxl}px ${Spacing.xxxl}px;
  text-align: center;
  margin-bottom: ${Spacing.xxxxl}px;
  transition: all 200ms ease;
  position: relative;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
  }
`;

const ProfileImageContainer = styled.div`
  margin-bottom: ${Spacing.md}px;
`;

const ProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 60px;
  background: linear-gradient(135deg, ${Colors.secondary}, ${Colors.accent});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xxxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textOnPrimary};
  box-shadow: ${Shadows.lg};
  border: 4px solid ${Colors.surface};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    padding: 2px;
    background: linear-gradient(135deg, ${Colors.secondary}, ${Colors.accent});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
  }
`;

const EmployeeName = styled.h1`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: ${Spacing.xl}px 0 ${Spacing.sm}px 0;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const EmployeePosition = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textSecondary};
  margin: 0 0 ${Spacing.sm}px 0;
`;

const EmployeeDepartment = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textTertiary};
  margin: 0 0 ${Spacing.xl}px 0;
  font-weight: ${FontWeights.medium};
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.xl}px;
`;

const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;

  @media (max-width: ${Breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing.lg}px;
  }

  @media (max-width: ${Breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  text-align: center;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
  }
`;

const MetricValue = styled.div<{ color?: string }>`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${props => props.color || Colors.textPrimary};
  margin-bottom: ${Spacing.sm}px;
  line-height: 0.9;
  letter-spacing: -0.02em;
`;

const MetricLabel = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.section`
  margin-bottom: ${Spacing.xxxxl}px;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.semibold};
  margin: 0 0 ${Spacing.xl}px 0;
  color: ${Colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.1;
  padding-bottom: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.border};
  
  /* Ultra-clean underline */
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${Colors.secondary};
    border-radius: 1px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.md}px;
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm}px;
  margin-top: ${Spacing.sm}px;
`;

const SkillChip = styled.span`
  background: ${Colors.secondary + '20'};
  color: ${Colors.secondary};
  border: 1px solid ${Colors.secondary + '40'};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  transition: all 150ms ease;
  
  &:hover {
    background: ${Colors.secondary + '30'};
    border-color: ${Colors.secondary};
    transform: translateY(-1px);
    box-shadow: ${Shadows.sm};
  }
`;

const SuggestionCard = styled.div<{ type: string }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-left: 4px solid ${props => 
    props.type === 'LEADERSHIP' ? Colors.secondary :
    props.type === 'TECHNICAL' ? Colors.info :
    Colors.success
  };
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  margin-bottom: ${Spacing.xl}px;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${props => 
      props.type === 'LEADERSHIP' ? Colors.secondary :
      props.type === 'TECHNICAL' ? Colors.info :
      Colors.success
    };
    transform: translateY(-2px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SuggestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm}px;

  @media (max-width: ${Breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.sm}px;
  }
`;

const TypeBadge = styled.span<{ type: string }>`
  background: ${props => 
    props.type === 'LEADERSHIP' ? Colors.secondary + '20' :
    props.type === 'TECHNICAL' ? Colors.info + '20' :
    Colors.success + '20'
  };
  color: ${props => 
    props.type === 'LEADERSHIP' ? Colors.secondary :
    props.type === 'TECHNICAL' ? Colors.info :
    Colors.success
  };
  padding: ${Spacing.xs}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Duration = styled.span`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
`;

const SuggestionTitle = styled.h4`
  font-size: ${FontSizes.md}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const SuggestionDescription = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  line-height: 1.5;
  margin-bottom: ${Spacing.md}px;
`;

const ResourcesContainer = styled.div`
  margin-bottom: ${Spacing.md}px;
`;

const ResourcesLabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-bottom: ${Spacing.xs}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResourceItem = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  line-height: 1.8;
`;

const EnrollButton = styled.button`
  background-color: ${Colors.accent};
  color: ${Colors.background};
  border: none;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${Colors.secondary};
  }
`;

const PotentialCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxl}px;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
    transform: translateY(-2px);
  }
`;

const PotentialHeader = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: ${Spacing.md}px;
`;

const PotentialScore = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.secondary};
  margin-right: ${Spacing.md}px;
  line-height: 0.9;
  letter-spacing: -0.02em;
`;

const PotentialLabel = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  font-weight: ${FontWeights.medium};
`;

const PotentialBar = styled.div`
  height: 12px;
  background-color: ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  overflow: hidden;
  margin-bottom: ${Spacing.md}px;
`;

const PotentialProgress = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, ${props => props.color}, ${Colors.secondary});
  border-radius: ${BorderRadius.sm}px;
  transition: width 0.6s ease;
`;

const PotentialInsight = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  margin: 0;
  font-weight: ${FontWeights.medium};
`;

const InfoRow = styled.div`
  display: flex;
  padding: ${Spacing.sm}px 0;
  border-bottom: 1px solid ${Colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  flex: 1;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
`;

const InfoValue = styled.span`
  flex: 2;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

// Loading animation
const PulseAnimation = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// Empty state components
const EmptyState = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xxxxl}px;
  text-align: center;
  margin-top: ${Spacing.xxxxl}px;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${Spacing.xl}px;
  opacity: 0.7;
`;

const EmptyStateTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.lg}px 0;
`;

const EmptyStateMessage = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
`;

// Add the animation to the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = PulseAnimation;
  document.head.appendChild(style);
}

// Profile Selector Components
const ProfileSelector = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.card};
  padding: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xxxxl}px;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.borderAccent};
  }
`;

const ProfileSelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg}px;
`;

const ProfileSelectorTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  letter-spacing: -0.01em;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${Spacing.md}px;

  @media (max-width: ${Breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing.sm}px;
  }

  @media (max-width: ${Breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ProfileCard = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? Colors.secondary + '10' : Colors.surface};
  border: 1px solid ${props => props.isActive ? Colors.secondary : Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
  cursor: pointer;
  transition: all 150ms ease;
  text-align: left;
  
  &:hover {
    border-color: ${Colors.secondary};
    background: ${Colors.secondary + '05'};
    transform: translateY(-1px);
    box-shadow: ${Shadows.sm};
  }
  
  &:focus {
    outline: none;
    border-color: ${Colors.borderFocus};
    box-shadow: 0 0 0 3px ${Colors.borderFocus}20;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProfileCardName = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
  line-height: 1.3;
`;

const ProfileCardRole = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xs}px;
  font-weight: ${FontWeights.normal};
`;

const ProfileCardLocation = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  
  &::before {
    content: '📍';
    font-size: ${FontSizes.sm}px;
  }
`;

const EmployeeProfileScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentRole, department } = useSelector((state: RootState) => state.user);
  const { employees, selectedEmployee } = useSelector((state: RootState) => state.employees);
  const [developmentSuggestions, setDevelopmentSuggestions] = useState<DevelopmentSuggestion[]>([]);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilters, setActiveFilters] = useState<{department?: string; location?: string; roleLevel?: string}>({});
  
  // Get role-based configuration
  const dashboardConfig = getRoleDashboardConfig(currentRole);
  
  // Simulate enterprise-scale data (filtered by role)
  const TOTAL_EMPLOYEES = dashboardConfig.maxEmployeesVisible === -1 ? DataConsistencyService.getTotalEmployees() : dashboardConfig.maxEmployeesVisible;
  const ITEMS_PER_PAGE = 12;

  // Get featured employees for initial display
  const featuredEmployees = EmployeeService.getFeaturedEmployees();
  
  // Apply role-based filtering to employee profiles
  const employeeProfiles = filterEmployees(featuredEmployees, currentRole, department);

  const employee: Employee = selectedEmployee || currentEmployee || employeeProfiles[0];

  useEffect(() => {
    initializeEmployeeData();
  }, []);

  useEffect(() => {
    if (currentEmployee) {
      loadDevelopmentSuggestions();
    }
  }, [currentEmployee]);

  const initializeEmployeeData = async () => {
    setInitialLoading(true);
    try {
      // Load featured employees into store
      const featuredEmployees = EmployeeService.getFeaturedEmployees();
      dispatch(setEmployees(featuredEmployees));
      
      // Load initial search results
      await performSearch('', {}, 1);
      
      // Load development suggestions for first employee
      loadDevelopmentSuggestions();
    } catch (error) {
      console.error('Failed to initialize employee data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadDevelopmentSuggestions = () => {
    // Generate localized development suggestions
    const suggestions: DevelopmentSuggestion[] = [
      {
        id: '1',
        employeeId: employee.id,
        type: 'LEADERSHIP',
        title: t('employee.suggestions.leadership.title'),
        description: t('employee.suggestions.leadership.description'),
        priority: 1,
        estimatedDuration: t('employee.suggestions.leadership.duration'),
        resources: t('employee.suggestions.leadership.resources', { returnObjects: true }) as string[],
      },
      {
        id: '2',
        employeeId: employee.id,
        type: 'TECHNICAL',
        title: t('employee.suggestions.technical.title'),
        description: t('employee.suggestions.technical.description'),
        priority: 2,
        estimatedDuration: t('employee.suggestions.technical.duration'),
        resources: t('employee.suggestions.technical.resources', { returnObjects: true }) as string[],
      },
      {
        id: '3',
        employeeId: employee.id,
        type: 'SOFT_SKILL',
        title: t('employee.suggestions.softSkill.title'),
        description: t('employee.suggestions.softSkill.description'),
        priority: 3,
        estimatedDuration: t('employee.suggestions.softSkill.duration'),
        resources: t('employee.suggestions.softSkill.resources', { returnObjects: true }) as string[],
      },
    ];
    
    setDevelopmentSuggestions(suggestions);
  };

  const getScoreColor = (score: number, max: number = 100) => {
    const percentage = (score / max) * 100;
    if (percentage >= 80) return Colors.success;
    if (percentage >= 60) return Colors.warning;
    return Colors.danger;
  };

  const handleEnrollEmployee = (suggestionTitle: string) => {
    AccessibilityUtils.announce(t('accessibility.enrolling', { name: employee.name, program: suggestionTitle }));
    console.log('Enrolling employee:', employee.name, 'in:', suggestionTitle);
  };

  // Perform enterprise search using the service
  const performSearch = async (
    query: string, 
    filters: {department?: string; location?: string; roleLevel?: string}, 
    page: number
  ) => {
    setLoading(true);
    try {
      const result = await EmployeeService.searchEmployees(query, filters, page, ITEMS_PER_PAGE);
      setSearchResults(result.employees);
      setTotalResults(result.total);
      setTotalPages(result.pages);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setTotalResults(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    performSearch(query, activeFilters, 1);
  };

  const handleFilterToggle = (filterKey: string, filterType: 'department' | 'location' | 'roleLevel') => {
    const newFilters = { ...activeFilters };
    
    if (newFilters[filterType] === filterKey) {
      delete newFilters[filterType];
    } else {
      newFilters[filterType] = filterKey;
    }
    
    setActiveFilters(newFilters);
    setCurrentPage(1);
    performSearch(searchQuery, newFilters, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    performSearch(searchQuery, activeFilters, page);
  };

  const handleEmployeeSelect = (selectedEmployee: Employee) => {
    setCurrentEmployee(selectedEmployee);
    dispatch(setSelectedEmployee(selectedEmployee));
    AccessibilityUtils.announce(t('accessibility.viewingProfile', { name: selectedEmployee.name }));
  };

  // Show initial loading state
  if (initialLoading) {
    return (
      <ScreenContainer>
        <div className="container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px' 
          }}>
            <LoadingSpinner />
          </div>
        </div>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <div className="container">
        <UnifiedPageHeader
          title={t('profiles.title')}
          subtitle="Search and analyze detailed employee profiles and development opportunities"
          metadata={{
            timestamp: new Date(),
            count: TOTAL_EMPLOYEES,
            status: loading ? t('profiles.searching') : `${totalResults.toLocaleString()} ${t('profiles.searchResults', { count: totalResults })}`
          }}
          showDivider={false}
        />
        
        <ProfileSelector>
          
          {/* Enterprise Search Input */}
          <div style={{ marginBottom: Spacing.lg + 'px', position: 'relative' }}>
            <input
              type="text"
              placeholder={t('profiles.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: '100%',
                padding: Spacing.md + 'px ' + Spacing.lg + 'px ' + Spacing.md + 'px ' + (Spacing.xxxxl + 8) + 'px',
                border: `1px solid ${Colors.border}`,
                borderRadius: BorderRadius.md + 'px',
                fontFamily: FontStacks.primary,
                fontSize: FontSizes.md + 'px',
                background: Colors.surface,
                color: Colors.textPrimary,
                transition: 'all 150ms ease'
              }}
            />
            <div style={{
              position: 'absolute',
              left: Spacing.lg + 'px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: Colors.textTertiary,
              fontSize: FontSizes.lg + 'px'
            }}>
              🔍
            </div>
          </div>

          {/* Quick Filters */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: Spacing.sm + 'px', 
            marginBottom: Spacing.lg + 'px' 
          }}>
            {[
              { key: 'Engineering', label: t('profiles.filters.engineering'), type: 'department' as const },
              { key: 'Sales', label: t('profiles.filters.sales'), type: 'department' as const },
              { key: 'Marketing', label: t('profiles.filters.marketing'), type: 'department' as const },
              { key: 'Senior', label: t('profiles.filters.senior'), type: 'roleLevel' as const },
              { key: 'Lead', label: t('profiles.filters.lead'), type: 'roleLevel' as const },
              { key: 'Germany', label: t('profiles.filters.germany'), type: 'location' as const },
              { key: 'USA', label: t('profiles.filters.usa'), type: 'location' as const },
              { key: 'India', label: t('profiles.filters.india'), type: 'location' as const }
            ].map(filter => {
              const isActive = activeFilters[filter.type] === filter.key;
              return (
                <button
                  key={filter.key}
                  onClick={() => handleFilterToggle(filter.key, filter.type)}
                  style={{
                    background: isActive ? Colors.secondary : 'transparent',
                    color: isActive ? Colors.textOnPrimary : Colors.textSecondary,
                    border: `1px solid ${isActive ? Colors.secondary : Colors.border}`,
                    padding: Spacing.xs + 'px ' + Spacing.md + 'px',
                    borderRadius: BorderRadius.sm + 'px',
                    fontFamily: FontStacks.primary,
                    fontSize: FontSizes.sm + 'px',
                    cursor: 'pointer',
                    transition: 'all 150ms ease'
                  }}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          {/* Search Results Grid */}
          <ProfileGrid>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <div key={i} style={{
                  background: Colors.surface,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.md + 'px',
                  padding: Spacing.lg + 'px',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}>
                  <div style={{ 
                    height: '20px', 
                    background: Colors.borderLight, 
                    borderRadius: '4px', 
                    marginBottom: Spacing.sm + 'px' 
                  }}></div>
                  <div style={{ 
                    height: '16px', 
                    background: Colors.borderLight, 
                    borderRadius: '4px', 
                    width: '70%' 
                  }}></div>
                </div>
              ))
            ) : (
              (searchResults.length > 0 ? searchResults : featuredEmployees.slice(0, 6)).map((profile) => (
                <ProfileCard
                  key={profile.id}
                  isActive={profile.id === currentEmployee?.id || profile.id === selectedEmployee?.id}
                  onClick={() => handleEmployeeSelect(profile)}
                  aria-label={t('profiles.viewProfile', { name: profile.name })}
                >
                  <ProfileCardName>{profile.name}</ProfileCardName>
                  <ProfileCardRole>{profile.position}</ProfileCardRole>
                  <ProfileCardLocation>
                    {profile.location} • {profile.performanceScore.toFixed(1)}/5
                  </ProfileCardLocation>
                </ProfileCard>
              ))
            )}
          </ProfileGrid>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: Spacing.md + 'px',
              marginTop: Spacing.xl + 'px',
              paddingTop: Spacing.lg + 'px',
              borderTop: `1px solid ${Colors.border}`
            }}>
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                style={{
                  padding: Spacing.sm + 'px ' + Spacing.md + 'px',
                  border: `1px solid ${Colors.border}`,
                  background: Colors.surface,
                  color: Colors.textSecondary,
                  borderRadius: BorderRadius.sm + 'px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
{t('profiles.previous')}
              </button>
              
              <span style={{ 
                color: Colors.textSecondary, 
                fontSize: FontSizes.sm + 'px',
                fontFamily: FontStacks.primary
              }}>
                {t('profiles.page', { current: currentPage, total: totalPages.toLocaleString() })}
              </span>
              
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                style={{
                  padding: Spacing.sm + 'px ' + Spacing.md + 'px',
                  border: `1px solid ${Colors.border}`,
                  background: Colors.surface,
                  color: Colors.textSecondary,
                  borderRadius: BorderRadius.sm + 'px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
{t('profiles.next')}
              </button>
            </div>
          )}
        </ProfileSelector>

        {/* Employee Profile - Only shown when an employee is selected */}
        {currentEmployee ? (
          <>
            <ProfileHeader>
          <ProfileImageContainer>
            <ProfileImage>
              {employee.name.split(' ').map(n => n[0]).join('')}
            </ProfileImage>
          </ProfileImageContainer>
          <EmployeeName>{employee.name}</EmployeeName>
          <EmployeePosition>{employee.position}</EmployeePosition>
          <EmployeeDepartment>{employee.department} • {employee.location}</EmployeeDepartment>
        </ProfileHeader>

        <MetricsContainer>
          <MetricCard>
            <MetricValue>{employee.tenure.toFixed(1)}</MetricValue>
            <MetricLabel>{t('employee.metrics.years')}</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue color={getScoreColor(employee.performanceScore, 5)}>
              {employee.performanceScore.toFixed(1)}/5
            </MetricValue>
            <MetricLabel>{t('employee.metrics.performance')}</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue color={getScoreColor(employee.engagementScore, 10)}>
              {employee.engagementScore.toFixed(1)}
            </MetricValue>
            <MetricLabel>{t('employee.metrics.engagement')}</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue color={getScoreColor(100 - employee.attritionRisk)}>
              {Math.round(employee.attritionRisk)}%
            </MetricValue>
            <MetricLabel>{t('employee.metrics.flightRisk')}</MetricLabel>
          </MetricCard>
        </MetricsContainer>

        <Section>
          <SectionTitle>{t('employee.sections.successionReadiness')}</SectionTitle>
          <SuccessionReadinessMeter
            readiness={employee.successionReadiness}
            targetRole="Engineering Manager"
          />
        </Section>

        <Section>
          <SectionTitle>{t('employee.sections.skills')}</SectionTitle>
          <SkillsContainer>
            {employee.skills.map((skill, index) => (
              <SkillChip key={index}>{skill}</SkillChip>
            ))}
          </SkillsContainer>
        </Section>

        <Section>
          <SectionTitle>{t('employee.sections.development')}</SectionTitle>
          <SectionSubtitle>{t('employee.sections.developmentSubtitle')}</SectionSubtitle>
          
          {developmentSuggestions.map((suggestion) => (
            <SuggestionCard key={suggestion.id} type={suggestion.type}>
              <SuggestionHeader>
                <TypeBadge type={suggestion.type}>{suggestion.type}</TypeBadge>
                <Duration>{suggestion.estimatedDuration}</Duration>
              </SuggestionHeader>
              
              <SuggestionTitle>{suggestion.title}</SuggestionTitle>
              <SuggestionDescription>{suggestion.description}</SuggestionDescription>
              
              <ResourcesContainer>
                <ResourcesLabel>{t('employee.development.resources')}</ResourcesLabel>
                {suggestion.resources.map((resource, index) => (
                  <ResourceItem key={index}>• {resource}</ResourceItem>
                ))}
              </ResourcesContainer>
              
              <EnrollButton onClick={() => handleEnrollEmployee(suggestion.title)}>
                {t('employee.development.enroll')}
              </EnrollButton>
            </SuggestionCard>
          ))}
        </Section>

        <Section>
          <SectionTitle>{t('employee.sections.leadership')}</SectionTitle>
          <PotentialCard>
            <PotentialHeader>
              <PotentialScore>{Math.round(employee.leadershipPotential)}%</PotentialScore>
              <PotentialLabel>{t('employee.leadership.score')}</PotentialLabel>
            </PotentialHeader>
            <PotentialBar>
              <PotentialProgress
                width={employee.leadershipPotential}
                color={getScoreColor(employee.leadershipPotential)}
              />
            </PotentialBar>
            <PotentialInsight>
              {t('employee.leadership.insight')}
            </PotentialInsight>
          </PotentialCard>
        </Section>

        <Section>
          <SectionTitle>{t('employee.sections.information')}</SectionTitle>
          <div>
            <InfoRow>
              <InfoLabel>{t('employee.info.email')}</InfoLabel>
              <InfoValue>{employee.email}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('employee.info.manager')}</InfoLabel>
              <InfoValue>{employee.manager}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('employee.info.hireDate')}</InfoLabel>
              <InfoValue>{employee.hireDate}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('employee.info.lastPromotion')}</InfoLabel>
              <InfoValue>{employee.lastPromotion || t('employee.info.na')}</InfoValue>
            </InfoRow>
          </div>
        </Section>
          </>
        ) : (
          /* Empty state when no employee is selected */
          <EmptyState>
            <EmptyStateIcon>👤</EmptyStateIcon>
            <EmptyStateTitle>{t('profiles.selectEmployee')}</EmptyStateTitle>
            <EmptyStateMessage>
              {t('profiles.selectEmployeeMessage', { count: TOTAL_EMPLOYEES.toLocaleString() })}
            </EmptyStateMessage>
          </EmptyState>
        )}
      </div>
    </ScreenContainer>
  );
};

export default EmployeeProfileScreen;